import { useEffect, useState } from 'react';
import { FormHelperText, Grid, InputLabel, MenuItem, OutlinedInput, Select } from '@mui/material';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import { FormDrawer, FormDrawerGroup } from '../../../../components/form-drawer';
import { parseNumberInput } from '../../../../utils/format';
import { ORGAO_FISCALIZADOR_LABEL, SITUACAO_AUDITORIA_LABEL, enumOptions } from '../../../../model/apm/apm-enums';
import { IAuditAddEditRequest } from '../../../../model/apm/audit/request/audit-add-edit-request';
import { IAuditResponse } from '../../../../model/apm/audit/response/audit-response';

interface AuditDrawerProps {
    open: boolean;
    initial?: IAuditResponse;
    isSaving: boolean;
    onClose: () => void;
    onSave: (value: IAuditAddEditRequest) => void;
}

// Mesmos limites da API: exercício entre 1900 e o ano seguinte
const MIN_YEAR = 1900;
const maxYear = () => new Date().getFullYear() + 1;

export function AuditDrawer({ open, initial, isSaving, onClose, onSave }: AuditDrawerProps) {
    const [form, setForm] = useState<IAuditAddEditRequest>({});
    const [showValidationErrors, setShowValidationErrors] = useState(false);
    const isEdit = !!initial;
    const yearInvalid = !form.year || form.year < MIN_YEAR || form.year > maxYear();
    const errors = {
        status: showValidationErrors && !form.status,
        inspectionBody: showValidationErrors && !form.inspectionBody,
        year: showValidationErrors && yearInvalid,
        unitName: showValidationErrors && !form.unitName?.trim(),
        processNumber: showValidationErrors && !form.processNumber?.trim(),
    };

    useEffect(() => {
        if (!open) return;
        setForm(initial
            ? {
                status: initial.status,
                inspectionBody: initial.inspectionBody,
                unitName: initial.unitName,
                processNumber: initial.processNumber,
                originProcessNumber: initial.originProcessNumber,
                year: initial.year,
                url: initial.url,
            }
            : { year: new Date().getFullYear() });
        setShowValidationErrors(false);
    }, [open, initial]);

    function update(patch: Partial<IAuditAddEditRequest>) {
        setForm((current) => ({ ...current, ...patch }));
    }

    function handleSave() {
        if (!form.status || !form.inspectionBody || yearInvalid || !form.unitName?.trim() || !form.processNumber?.trim()) {
            setShowValidationErrors(true);
            return;
        }
        onSave({
            ...form,
            unitName: form.unitName.trim(),
            processNumber: form.processNumber.trim(),
            originProcessNumber: form.originProcessNumber?.trim() || undefined,
            url: form.url?.trim() || undefined,
        });
    }

    return (
        <FormDrawer
            open={open}
            icon={<FactCheckIcon />}
            title={isEdit ? 'Editar auditoria' : 'Nova auditoria'}
            subtitle={isEdit ? `Processo ${initial?.processNumber || ''}` : 'Prestação de contas enviada a órgão fiscalizador'}
            saveLabel={isEdit ? 'Salvar alterações' : 'Adicionar auditoria'}
            isSaving={isSaving}
            onClose={onClose}
            onSave={handleSave}
        >
            <FormDrawerGroup title="Identificação">
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <InputLabel required>Órgão fiscalizador</InputLabel>
                        <Select
                            fullWidth
                            autoFocus
                            value={form.inspectionBody ?? ''}
                            onChange={(e) => update({ inspectionBody: e.target.value === '' ? undefined : Number(e.target.value) })}
                            error={errors.inspectionBody}
                        >
                            {enumOptions(ORGAO_FISCALIZADOR_LABEL).map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                        </Select>
                        {errors.inspectionBody && <FormHelperText error>Órgão fiscalizador é obrigatório</FormHelperText>}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <InputLabel required>Exercício</InputLabel>
                        <OutlinedInput
                            fullWidth
                            type="number"
                            inputProps={{ min: MIN_YEAR, max: maxYear() }}
                            value={form.year ?? ''}
                            onChange={(e) => update({ year: parseNumberInput(e.target.value) })}
                            error={errors.year}
                        />
                        {errors.year && <FormHelperText error>Informe um exercício entre {MIN_YEAR} e {maxYear()}</FormHelperText>}
                    </Grid>
                    <Grid item xs={12}>
                        <InputLabel required>Unidade</InputLabel>
                        <OutlinedInput
                            fullWidth
                            autoComplete="off"
                            inputProps={{ maxLength: 250 }}
                            value={form.unitName || ''}
                            onChange={(e) => update({ unitName: e.target.value })}
                            error={errors.unitName}
                        />
                        {errors.unitName && <FormHelperText error>Unidade é obrigatória</FormHelperText>}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <InputLabel required>Número do processo</InputLabel>
                        <OutlinedInput
                            fullWidth
                            autoComplete="off"
                            inputProps={{ maxLength: 50 }}
                            value={form.processNumber || ''}
                            onChange={(e) => update({ processNumber: e.target.value })}
                            error={errors.processNumber}
                        />
                        {errors.processNumber && <FormHelperText error>Número do processo é obrigatório</FormHelperText>}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <InputLabel>Processo de origem</InputLabel>
                        <OutlinedInput
                            fullWidth
                            autoComplete="off"
                            inputProps={{ maxLength: 50 }}
                            value={form.originProcessNumber || ''}
                            onChange={(e) => update({ originProcessNumber: e.target.value })}
                        />
                    </Grid>
                </Grid>
            </FormDrawerGroup>

            <FormDrawerGroup title="Situação" description="Andamento do processo no órgão fiscalizador e link para consulta pública.">
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <InputLabel required>Situação</InputLabel>
                        <Select
                            fullWidth
                            value={form.status ?? ''}
                            onChange={(e) => update({ status: e.target.value === '' ? undefined : Number(e.target.value) })}
                            error={errors.status}
                        >
                            {enumOptions(SITUACAO_AUDITORIA_LABEL).map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                        </Select>
                        {errors.status && <FormHelperText error>Situação é obrigatória</FormHelperText>}
                    </Grid>
                    <Grid item xs={12}>
                        <InputLabel>Link do processo</InputLabel>
                        <OutlinedInput
                            fullWidth
                            autoComplete="off"
                            placeholder="https://"
                            value={form.url || ''}
                            onChange={(e) => update({ url: e.target.value })}
                        />
                    </Grid>
                </Grid>
            </FormDrawerGroup>
        </FormDrawer>
    );
}
